<template>
  <Container title="储备金记录">
    <!-- ★ 新增：最外层 flex 容器 -->
    <div class="flex-page-layout">
      <!-- 搜索卡片 -->
      <el-card shadow="never" class="search-card">
        <el-form :model="searchForm" label-position="left" label-width="60px" @submit.prevent @keyup.enter="handleSearch">
          <el-row :gutter="20">
            <!-- 企业 (仅超管) -->
            <el-col :xs="24" :sm="12" :md="6" :lg="6" v-if="isSuperAdmin">
              <el-form-item label="企业">
                <el-select v-model="searchForm.enterpriseId" placeholder="请选择企业" clearable style="width: 100%;" @change="handleEnterpriseChange">
                  <el-option v-for="item in enterpriseList" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
              </el-form-item>
            </el-col>
            
            <!-- 站点 (仅超管) -->
            <el-col :xs="24" :sm="12" :md="6" :lg="6" v-if="isSuperAdmin">
              <el-form-item label="站点">
                <el-select v-model="searchForm.stationId" placeholder="请选择站点" clearable style="width: 100%;" :disabled="!searchForm.enterpriseId">
                  <el-option v-for="item in stationOptions" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
              </el-form-item>
            </el-col>
            
            <!-- 种类 -->
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-form-item label="种类">
                <el-select v-model="searchForm.type" placeholder="请选择种类" clearable style="width: 100%;">
                  <el-option v-for="opt in typeOptions" :key="opt.value" :label="opt.label" :value="opt.value">
                    <span class="type-option">
                      <i class="type-dot" :style="{ background: opt.color }"></i>
                      <span :style="{ color: opt.color }">{{ opt.label }}</span>
                    </span>
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            
            <!-- 订单号 -->
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-form-item label="订单号">
                <el-input v-model="searchForm.orderNo" placeholder="请输入订单号" clearable style="width: 100%;" />
              </el-form-item>
            </el-col>
            
            <!-- 时间 -->
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-form-item label="时间">
                <el-date-picker v-model="searchForm.dateRange" type="daterange" range-separator="至" start-placeholder="开始日期"
                  end-placeholder="结束日期" value-format="YYYY-MM-DD" :shortcuts="shortcuts" style="width: 100%;" />
              </el-form-item>
            </el-col>
            
            <!-- 按钮区域 -->
            <el-col :xs="24" :sm="12" :md="6" :lg="6" style="display: flex; justify-content: flex-end;">
              <el-form-item label=" " label-width="0">
                <div style="display: flex; gap: 8px;">
                  <el-button type="primary" @click="handleSearch">查询</el-button>
                  <el-button @click="resetSearch">重置</el-button>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>

      <!-- 表格卡片 -->
      <el-card shadow="never" class="table-card">
        <template #header>
          <div class="table-header">
            <div class="table-header-left"></div>
            <div class="table-header-right">
              <el-tooltip content="刷新" placement="bottom"><el-button circle @click="fetchData"><el-icon><Refresh /></el-icon></el-button></el-tooltip>
              <el-tooltip content="导出" placement="bottom"><el-button circle @click="handleExport"><el-icon><Download /></el-icon></el-button></el-tooltip>
              <el-tooltip content="打印" placement="bottom"><el-button circle @click="handlePrint"><el-icon><Printer /></el-icon></el-button></el-tooltip>
            </div>
          </div>
        </template>

        <!-- ★ 新增：表格包裹层 -->
        <div class="table-container">
          <el-table :data="tableData" border style="width: 100%" height="100%">
            <el-table-column prop="id" label="ID" min-width="70" align="center" />
            <el-table-column prop="station_name" label="站点" min-width="140" show-overflow-tooltip />
            <el-table-column label="种类" min-width="130" align="center">
              <template #default="{ row }"><el-tag :type="getTypeTag(row.type)" size="small">{{ getTypeText(row.type) }}</el-tag></template>
            </el-table-column>
            <el-table-column prop="order_no" label="订单编号" min-width="200" show-overflow-tooltip />
            <el-table-column label="结算金额" min-width="110" align="right">
              <template #default="{ row }">{{ Math.abs(Number(row.amount) || 0).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="剩余储备金" min-width="120" align="right">
              <template #default="{ row }">{{ (Number(row.balance_after) || 0).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="操作人/用户" min-width="120" prop="operator" show-overflow-tooltip />
            <el-table-column label="创建时间" min-width="160">
              <template #default="{ row }">{{ formatTime(row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </div>

        <div class="pagination-wrapper">
          <el-pagination
            v-model:current-page="page.current"
            v-model:page-size="page.pageSize"
            :total="total"
            layout="total, sizes, prev, pager, next, jumper"
            :page-sizes="[15, 40, 50, 100]"
            @size-change="onSizeChange"
            @current-change="fetchData"
          />
        </div>
      </el-card>
    </div>

    <!-- 新增弹窗 -->
    <el-dialog v-model="dialogVisible" title="新增储备金记录" width="500px" @close="closeDialog" destroy-on-close>
      <el-form :model="addForm" :rules="addRules" ref="addFormRef" label-width="80px">
        <el-form-item label="站点" prop="stationId">
          <el-select v-model="addForm.stationId" placeholder="请选择站点" style="width: 100%">
            <el-option v-for="item in allStations" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="类型" prop="type">
          <el-select v-model="addForm.type" placeholder="请选择类型" style="width: 100%">
            <el-option label="手动新增" :value="1" />
            <el-option label="手动减少" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item label="金额" prop="amount">
          <el-input-number v-model="addForm.amount" :min="0.01" :precision="2" :step="0.5" style="width: 100%" placeholder="请输入金额" />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="addForm.remark" type="textarea" rows="3" placeholder="请输入备注（选填）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeDialog">取消</el-button>
        <el-button v-if="$hasPermission('btn:add_reserve')" type="primary" @click="submitAdd" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <ExportDialog
      v-model:visible="exportDialogVisible"
      :fields="exportFields"
      :export-api="exportApi"
      :get-search-params="getSearchParams"
      :current-page="page"
      :default-file-name="defaultFileName()"
    />
  </Container>
</template>

<script>
import { pub } from '/common/event';
import moment from 'moment';
import { Plus, Refresh, Download, Printer } from '@element-plus/icons-vue';
import { dateShortcuts } from '../../../utils/shortcuts.js';
import enterpriseStationMixin from '@/mixins/enterpriseStationMixin';
import ExportDialog from '@/components/container/ExportDialog.vue';
import exportMixin from '@/mixins/exportMixin';

export default {
  components: { Plus, Refresh, Download, Printer, ExportDialog },
  mixins: [enterpriseStationMixin, exportMixin],
  data() {
    return {
      shortcuts: dateShortcuts,
      searchForm: { enterpriseId: null, stationId: null, type: null, orderNo: '', dateRange: null },
      allStations: [],
      tableData: [],
      total: 0,
      page: { current: 1, pageSize: 15 },
      dialogVisible: false,
      submitting: false,
      addForm: { stationId: null, type: null, amount: null, remark: '' },
      addRules: {
        stationId: [{ required: true, message: '请选择站点', trigger: 'change' }],
        type: [{ required: true, message: '请选择类型', trigger: 'change' }],
        amount: [{ required: true, message: '请输入金额', trigger: 'blur' }],
      },
      typeOptions: [
        { label: '手动新增', value: 1, color: '#67c23a' },
        { label: '手动减少', value: 2, color: '#f56c6c' },
        { label: '订单新增(退款)', value: 3, color: '#67c23a' },
        { label: '订单减少(支付)', value: 4, color: '#f56c6c' },
      ],
      exportFields: [
        { key: 'id', label: '序号列', checked: true },
        { key: 'station_name', label: '站点', checked: true },
        { key: 'type_label', label: '种类', checked: true },
        { key: 'order_no', label: '订单编号', checked: true },
        { key: 'amount', label: '结算金额', checked: true },
        { key: 'balance_after', label: '剩余储备金', checked: true },
        { key: 'operator', label: '操作人/用户', checked: true },
        { key: 'remark', label: '备注', checked: true },
        { key: 'created_at_label', label: '创建时间', checked: true },
      ],
    };
  },
  mounted() {
    const today = moment().format('YYYY-MM-DD');
    this.searchForm.dateRange = [today, today];
    
    if (!this.isSuperAdmin) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u.role === 'admin' && u.enterprise_id) {
          this.searchForm.enterpriseId = u.enterprise_id;
        } else if (u.role === 'station_leader' && u.station_id) {
          this.searchForm.stationId = u.station_id;
        }
      } catch (e) {}
    }
    this.fetchData();
  },
  computed: {
    exportApi() {
      return this.$api.reserve.export;
    },
    isSuperAdmin() {
      const u1 = this.$store.state.user?.user;
      if (u1 && u1.role) return u1.role === 'super_admin';
      try {
        const u2 = JSON.parse(localStorage.getItem('user') || '{}');
        return u2.role === 'super_admin' || u2.role_id === 1;
      } catch (e) { return false; }
    },
  },
  methods: {
    defaultFileName() {
      return '储备金数据';
    },
    formatTime(ts) {
      if (!ts) return '';
      return moment(ts).format('YYYY-MM-DD HH:mm:ss');
    },
    getTypeText(type) {
      const map = { 1: '手动新增', 2: '手动减少', 3: '订单新增(退款)', 4: '订单减少(支付)' };
      return map[type] || '未知';
    },
    getTypeTag(type) {
      const map = { 1: 'success', 2: 'danger', 3: 'success', 4: 'danger' };
      return map[type] || 'info';
    },
    async fetchData() {
      try {
        const params = {
          page: this.page.current,
          pageSize: this.page.pageSize,
          enterpriseId: this.searchForm.enterpriseId || undefined,
          stationId: this.searchForm.stationId || undefined,
          type: this.searchForm.type || undefined,
          orderNo: this.searchForm.orderNo || undefined,
        };
        if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
          params.startTime = new Date(this.searchForm.dateRange[0]).getTime();
          params.endTime = new Date(this.searchForm.dateRange[1]).getTime() + 24 * 60 * 60 * 1000 - 1;
        }
        const ret = await this.$api.reserve.page(params);
        if (ret && ret.success) {
          this.tableData = ret.data.data || [];
          this.total = ret.data.total || 0;
          if (ret.data.page) {
            this.page.current = ret.data.page.current;
            this.page.pageSize = ret.data.page.pageSize;
          }
        } else {
          pub('error_message', ret?.message || '加载数据失败');
        }
      } catch (e) {
        console.error('加载储备金记录失败:', e);
        pub('error_message', '请求失败');
      }
    },
    onSizeChange() {
      this.page.current = 1;
      this.fetchData();
    },
    handleSearch() {
      this.page.current = 1;
      this.fetchData();
    },
    resetSearch() {
      this.searchForm = { enterpriseId: null, stationId: null, type: null, orderNo: '', dateRange: null };
      this.stationOptions = [];
      this.page.current = 1;
      this.fetchData();
    },
    openAddDialog() {
      this.dialogVisible = true;
      this.addForm = { stationId: null, type: null, amount: null, remark: '' };
      this.$nextTick(() => { this.$refs.addFormRef?.clearValidate(); });
      this.loadAllStations();
    },
    closeDialog() {
      this.dialogVisible = false;
      this.addForm = { stationId: null, type: null, amount: null, remark: '' };
    },
    async loadAllStations() {
      try {
        const ret = await this.$api.station.page({ status: 1, pageSize: 1000 });
        if (ret && ret.success) {
          this.allStations = ret.data?.data || ret.data || [];
        }
      } catch (e) {
        console.error('加载站点列表失败:', e);
      }
    },
    async submitAdd() {
      try { await this.$refs.addFormRef.validate(); } catch (e) { return; }
      this.submitting = true;
      try {
        const data = {
          stationId: this.addForm.stationId,
          type: this.addForm.type,
          amount: this.addForm.amount,
          remark: this.addForm.remark || '',
        };
        const ret = await this.$api.reserve.manual(data);
        if (ret && ret.success) {
          pub('success_message', '新增储备金记录成功');
          this.closeDialog();
          this.fetchData();
        } else {
          pub('error_message', ret?.message || '操作失败');
        }
      } catch (e) {
        console.error('新增储备金记录失败:', e);
        pub('error_message', '请求失败');
      } finally {
        this.submitting = false;
      }
    },
    handleExport() {
      this.openExportDialog();
    },
    getSearchParams() {
      const params = { ...this.searchForm };
      if (params.dateRange && params.dateRange.length === 2) {
        params.startTime = new Date(params.dateRange[0]).getTime();
        params.endTime = new Date(params.dateRange[1]).getTime() + 24 * 60 * 60 * 1000 - 1;
        delete params.dateRange;
      }
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });
      return params;
    },
    handlePrint() {
      window.print();
    },
  },
};
</script>

<style scoped>
/* ★ 核心高度控制 */
.flex-page-layout { display: flex; flex-direction: column; height: calc(100vh - 120px); box-sizing: border-box; }
.search-card { flex-shrink: 0; margin-bottom: 16px; }
.table-card { flex: 1; display: flex; flex-direction: column; overflow: hidden; margin-bottom: 0; }
.table-card :deep(.el-card__body) { flex: 1; display: flex; flex-direction: column; overflow: hidden; padding: 16px; }
.table-container { flex: 1; overflow: hidden; }
.pagination-wrapper { flex-shrink: 0; margin-top: 16px; display: flex; justify-content: center; }

.table-card :deep(.el-card__header) { padding: 10px 16px; border-bottom: 1px solid #e4e7ed; }
.table-header { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.table-header-left { display: flex; align-items: center; gap: 8px; }
.table-header-right { display: flex; align-items: center; gap: 4px; }
.type-option { display: inline-flex; align-items: center; gap: 6px; }
.type-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
</style>